import { MoreVert } from '@mui/icons-material';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import React, { ReactNode, useState } from 'react';

export interface RowAction<T> {
  label: string;
  icon?: ReactNode;
  onClick: (row: T, index: number) => void;
  /** Per-row disabled state. */
  disabled?: (row: T) => boolean;
  /** Per-row hidden state — the item is omitted entirely. */
  hidden?: (row: T) => boolean;
}

interface Props<T> {
  row: T;
  actions: RowAction<T>[];
}

/**
 * Kebab (⋮) menu of row actions, for use in a TanStack display column's
 * `cell` renderer. TanStack is headless — it has no built-in action UI —
 * so this is the shared menu both TanStack admin sections render. (The MUI X
 * DataGrid sections use the grid's native `type: 'actions'` column instead,
 * which ships its own menu.)
 */
export function RowActionsMenu<T>({ row, actions }: Props<T>): React.JSX.Element | null {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = (): void => setAnchor(null);

  const visible = actions.filter((a) => !a.hidden?.(row));
  if (visible.length === 0) {
    return null;
  }

  return (
    <>
      <Tooltip title="Actions">
        <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}>
          <MoreVert fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={!!anchor} onClose={close}>
        {visible.map((action, index) => (
          <MenuItem
            key={action.label}
            disabled={action.disabled?.(row) ?? false}
            onClick={() => {
              close();
              action.onClick(row, index);
            }}
          >
            {action.icon && <ListItemIcon>{action.icon}</ListItemIcon>}
            <ListItemText>{action.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
